import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Button,
  Snackbar
} from '@mui/material';
import {
  ArrowBack,
  Description,
  Headphones,
  Lightbulb,
  Refresh
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { createApiClient } from '../../utils/api';
import { formatReportMonth, formatDuration } from '../../utils/formatters';
import { useCurrentUser } from '../../hooks/useApi';
import { Navigate } from 'react-router-dom';
import { API_URL } from '../../utils/config';
import { MarketReportDropzone } from '../../components/admin/MarketReportDropzone';

export const MarketDetailAdmin: React.FC = () => {
  const { marketId } = useParams<{ marketId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const api = createApiClient(authenticatedFetch);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Redirect if user doesn't have admin permission
  if (!user?.permissions?.includes('market_compass.admin')) {
    return <Navigate to="/" replace />;
  }

  // Fetch market details
  const { data: market, isLoading: loadingMarket } = useQuery({
    queryKey: ['admin', 'market', marketId],
    queryFn: async () => {
      const response = await api.get('/api/marketcompass/admin/markets/all');
      const markets = response.data?.markets || [];
      return markets.find((m: any) => m.id === parseInt(marketId!));
    }
  });

  // Fetch reports for this market
  const { data: reports, isLoading: loadingReports } = useQuery({
    queryKey: ['admin', 'market-reports', marketId],
    queryFn: async () => {
      const response = await api.get('/api/marketcompass/admin/reports/all');
      return response.data.reports.filter((r: any) => r.market_id === parseInt(marketId!));
    }
  });

  // Fetch podcasts for this market
  const { data: podcasts, isLoading: loadingPodcasts } = useQuery({
    queryKey: ['admin', 'market-podcasts', marketId],
    queryFn: async () => {
      const response = await api.get('/api/marketcompass/admin/podcasts/all');
      return response.data.podcasts.filter((p: any) => p.market_id === parseInt(marketId!));
    }
  });

  // Fetch key findings for this market
  const { data: keyFindings, isLoading: loadingKeyFindings } = useQuery({
    queryKey: ['admin', 'market-key-findings', marketId],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/admin/markets/${marketId}/key-findings`);
      if (!response || !response.ok) return [];
      const result = await response.json();
      return result.data || [];
    }
  });

  // Mutation to toggle market status
  const toggleMarketStatusMutation = useMutation({
    mutationFn: async (isActive: boolean) => {
      const response = await api.put(`/api/marketcompass/admin/markets/${marketId}/status`, {
        is_active: isActive
      });
      return response.data;
    },
    onSuccess: (data) => {
      setSnackbarMessage(data.message);
      setSnackbarOpen(true);
      // Invalidate market query to refresh data
      queryClient.invalidateQueries({ queryKey: ['admin', 'market', marketId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'markets'] });
    },
    onError: (error: any) => {
      setSnackbarMessage(error.response?.data?.error || 'Failed to update market status');
      setSnackbarOpen(true);
    }
  });

  const isLoading = loadingMarket || loadingReports || loadingPodcasts || loadingKeyFindings;

  if (isLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading market details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!market) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ my: 4 }}>
          Market not found.
        </Alert>
      </Container>
    );
  }

  const reportColumns: GridColDef[] = [
    {
      field: 'report_month',
      headerName: 'Month',
      width: 120,
      renderCell: (params) => formatReportMonth(params.value)
    },
    {
      field: 'short_description',
      headerName: 'Short Description',
      flex: 1,
      minWidth: 200
    },
    {
      field: 'medium_description',
      headerName: 'Medium Description',
      flex: 2,
      minWidth: 300
    },
    {
      field: 'file_name',
      headerName: 'File Name',
      width: 200
    },
    {
      field: 'is_published',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Published' : 'Draft'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      valueFormatter: (params) => new Date(params.value).toLocaleDateString()
    }
  ];

  const podcastColumns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 200
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 300
    },
    {
      field: 'duration_seconds',
      headerName: 'Duration',
      width: 100,
      renderCell: (params) => formatDuration(params.value)
    },
    {
      field: 'file_name',
      headerName: 'File Name',
      width: 200
    },
    {
      field: 'is_published',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Published' : 'Draft'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      valueFormatter: (params) => new Date(params.value).toLocaleDateString()
    }
  ];

  const keyFindingColumns: GridColDef[] = [
    {
      field: 'display_order',
      headerName: '#',
      width: 60,
      type: 'number'
    },
    {
      field: 'finding_text',
      headerName: 'Key Finding',
      flex: 1,
      minWidth: 400,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {params.value}
          </Typography>
          {params.row.description && params.row.description !== params.value && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {params.row.description}
            </Typography>
          )}
        </Box>
      )
    },
    {
      field: 'report_month',
      headerName: 'Report Month',
      width: 120,
      renderCell: (params) => formatReportMonth(params.value)
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value || 'General'}
          size="small"
          variant="outlined"
        />
      )
    }
  ];

  const handleRefresh = () => {
    // Invalidate all queries for this market to refresh data
    queryClient.invalidateQueries({ queryKey: ['admin', 'market', marketId] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'market-reports', marketId] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'market-podcasts', marketId] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'market-key-findings', marketId] });
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/admin/reports')}>
            <ArrowBack />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              {market?.name} - Market Details
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage reports, podcasts, and key findings for this market
            </Typography>
          </Box>
          <IconButton onClick={handleRefresh} size="large">
            <Refresh />
          </IconButton>
        </Box>

        {/* Market Information */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Market Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Continent
                </Typography>
                <Typography variant="h6">
                  {market.continent_name || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Country
                </Typography>
                <Typography variant="h6">
                  {market.country_name || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Market Name
                </Typography>
                <Typography variant="h6">
                  {market.name}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                  <Chip
                    label={market.is_active ? 'Active' : 'Inactive'}
                    color={market.is_active ? 'success' : 'default'}
                    size="medium"
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => toggleMarketStatusMutation.mutate(!market.is_active)}
                    disabled={toggleMarketStatusMutation.isPending}
                  >
                    {market.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Report Upload Dropzone */}
        <MarketReportDropzone
          marketId={parseInt(marketId!)}
          marketName={market.name}
          onProcessingComplete={handleRefresh}
        />

        {/* Reports Section */}
        <Paper sx={{ mb: 4, p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Description sx={{ mr: 1 }} />
            <Typography variant="h6">
              Reports ({reports?.length || 0})
            </Typography>
          </Box>
          <Box sx={{ height: 400 }}>
            <DataGrid
              rows={reports || []}
              columns={reportColumns}
              getRowId={(row) => row.id}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 }
                },
                sorting: {
                  sortModel: [{ field: 'report_month', sort: 'desc' }]
                }
              }}
              disableRowSelectionOnClick
              onRowClick={(params) => {
                navigate(`/admin/market/${marketId}/report/${params.row.id}`);
              }}
              sx={{
                '& .MuiDataGrid-row': {
                  cursor: 'pointer'
                }
              }}
            />
          </Box>
        </Paper>

        {/* Podcasts Section */}
        <Paper sx={{ mb: 4, p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Headphones sx={{ mr: 1 }} />
            <Typography variant="h6">
              Podcasts ({podcasts?.length || 0})
            </Typography>
          </Box>
          <Box sx={{ height: 400 }}>
            <DataGrid
              rows={podcasts || []}
              columns={podcastColumns}
              getRowId={(row) => row.id}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 }
                },
                sorting: {
                  sortModel: [{ field: 'created_at', sort: 'desc' }]
                }
              }}
              disableRowSelectionOnClick
              onRowClick={(params) => {
                navigate(`/admin/market/${marketId}/podcast/${params.row.id}`);
              }}
              sx={{
                '& .MuiDataGrid-row': {
                  cursor: 'pointer'
                }
              }}
            />
          </Box>
        </Paper>

        {/* Key Findings Section */}
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Lightbulb sx={{ mr: 1 }} />
            <Typography variant="h6">
              Key Findings ({keyFindings?.length || 0})
            </Typography>
          </Box>
          <Box sx={{ height: 400 }}>
            <DataGrid
              rows={keyFindings || []}
              columns={keyFindingColumns}
              getRowId={(row) => row.id}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 }
                },
                sorting: {
                  sortModel: [{ field: 'report_month', sort: 'desc' }, { field: 'display_order', sort: 'asc' }]
                }
              }}
              disableRowSelectionOnClick
            />
          </Box>
        </Paper>
      </Box>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
};